import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from "@/components/ui/button";
import { Strategy, Symbol } from './types';
import { useToast } from "@/hooks/use-toast";
import StewieQuantModelComponent from './StewieQuantModelComponent';
import StewieQuantStrategyConfig from './StewieQuantStrategyConfig';
import StewieQuantSymbolsAggregation from './StewieQuantSymbolsAggregation';
import { useRealMarketData } from './hooks/useRealMarketData';
import { 
  Plus, 
  ChevronLeft, 
  ChevronRight, 
  Settings, 
  LineChart, 
  Play,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

// Default values for better maintainability
const DEFAULT_PROFIT_TARGET = '5';
const DEFAULT_STOP_LOSS = '2';

interface AggregatedSymbol {
  id: string;
  symbol: string;
  profitTarget: string;
  stopLoss: string;
  direction?: 'long' | 'short' | 'auto';
  // Additional properties should be added explicitly as needed
  // Removed index signature for better type safety
}

interface StewieQuantDashboardIntegrationProps {
  isExpanded?: boolean;
  setIsExpanded?: (expanded: boolean) => void;
  onCreateStrategy?: (strategyData: any) => void;
  activeStrategy?: any;
  onFetchMarketData?: (symbols: AggregatedSymbol[]) => void;
}

const StewieQuantDashboardIntegration: React.FC<StewieQuantDashboardIntegrationProps> = ({
  isExpanded = false, 
  setIsExpanded, 
  onCreateStrategy, 
  activeStrategy = null,
  onFetchMarketData
}) => {
  const { toast } = useToast();
  const [modelStatus, setModelStatus] = useState('idle');
  const [modelResults, setModelResults] = useState(null);
  const [aggregatedSymbols, setAggregatedSymbols] = useState<AggregatedSymbol[]>([]);
  const [showAdvancedConfig, setShowAdvancedConfig] = useState(false);
  const [showResults, setShowResults] = useState(false);
  
  const {
    marketData,
    isLoading: isLoadingMarketData,
    fetchMarketData
  } = useRealMarketData();
  
  const [currentConfig, setCurrentConfig] = useState({
    correlationThreshold: 0.7,
    zscoreThreshold: 2.0,
    volatilityLookback: 20,
    maxPositionSizePct: 0.05,
    regimeChangeThreshold: 0.3,
    reversionHalfLife: 15,
    executionDelayMs: 500,
    adaptivePositionSizing: true,
    regimeDetection: true,
    multiAssetClustering: true,
    backtest: {
      startDate: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      endDate: new Date().toISOString().split('T')[0],
      initialCapital: 100000,
    }
  });

  useEffect(() => {
    if (activeStrategy?.params) {
      setCurrentConfig(prev => ({
        ...prev,
        ...activeStrategy.params
      }));
    }
    
    if (activeStrategy?.params?.aggregatedSymbols && 
        Array.isArray(activeStrategy.params.aggregatedSymbols)) {
      setAggregatedSymbols(activeStrategy.params.aggregatedSymbols);
    } else if (activeStrategy?.symbols) {
      const symbols = Array.isArray(activeStrategy.symbols) 
        ? activeStrategy.symbols.map((s: any) => ({
            id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
            symbol: typeof s === 'string' ? s : s.name,
            profitTarget: '',
            stopLoss: ''
          }))
        : activeStrategy.symbols.split(',').map((s: string) => ({
            id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
            symbol: s.trim(),
            profitTarget: '',
            stopLoss: ''
          }));
      
      setAggregatedSymbols(symbols);
    }
  }, [activeStrategy]);

  const handleSaveConfig = (config: any) => {
    setCurrentConfig(config);
    
    if (activeStrategy) {
      console.log('Updating strategy', activeStrategy.id, 'with config', config);
    }
  };

  const handleSaveSymbols = (symbols: AggregatedSymbol[]) => {
    setAggregatedSymbols(symbols);
    console.log('Saved symbols:', symbols);
    
    if (onFetchMarketData) {
      onFetchMarketData(symbols);
    } else {
      fetchMarketData(symbols).catch(error => {
        toast({
          title: "Market Data Error",
          description: error.message || "Failed to fetch market data",
          variant: "destructive"
        });
      });
    }
  };

  const handleRunOptimization = async () => {
    if (aggregatedSymbols.length === 0) {
      toast({
        title: "No Symbols",
        description: "Please add at least one symbol before running a backtest",
        variant: "destructive"
      });
      return;
    }
    
    setModelStatus('running');
    setShowResults(true);

    console.log('Running backtest with config', currentConfig);
    console.log('Using symbols:', aggregatedSymbols);
    console.log('Market data:', marketData);

    const optimizedSymbols: AggregatedSymbol[] = aggregatedSymbols.map(s => ({
      id: s.id,
      symbol: s.symbol,
      profitTarget: s.profitTarget || DEFAULT_PROFIT_TARGET,
      stopLoss: s.stopLoss || DEFAULT_STOP_LOSS,
      direction: s.direction || 'auto'
    }));

    console.log('Optimized symbols:', optimizedSymbols);
  };

  const handleModelResults = (results: any) => {
    setModelResults(results);
    console.log('Received model results', results);
  };

  const handleModelStatusChange = (status: string) => {
    setModelStatus(status);
  };

  const handleCreateStrategy = () => {
    if (!onCreateStrategy) {
      toast({
        title: "Error",
        description: "Strategy creation handler not available.",
        variant: "destructive"
      });
      return;
    }

    if (aggregatedSymbols.length === 0) {
      toast({
        title: "No Symbols",
        description: "Please add at least one symbol before creating a strategy.",
        variant: "destructive"
      });
      return;
    }

    // Create strategy with StewieQuant configuration
    const strategyData = {
      name: `StewieQuant Strategy - ${new Date().toLocaleDateString()}`,
      description: `AI-powered strategy using StewieQuant algorithms with ${aggregatedSymbols.length} symbols`,
      engine: 'stewie_quant',
      riskLevel: 'medium' as const,
      symbols: aggregatedSymbols.map(s => ({
        ticker: s.symbol,
        symbol: s.symbol,
        direction: s.direction || 'auto',
        weight: 1,
        profitTarget: s.profitTarget,
        stopLoss: s.stopLoss
      })),
      models: [{
        type: 'stewie_quant',
        weight: 1,
        decisionControl: 'signal_generation',
        stewieQuantSettings: {
          correlationThreshold: currentConfig.correlationThreshold,
          zscoreThreshold: currentConfig.zscoreThreshold,
          reversionHalfLife: currentConfig.reversionHalfLife,
          optimizerLevel: 2,
          adaptiveThreshold: 0.5
        }
      }],
      // Store StewieQuant-specific parameters in params
      params: {
        ...currentConfig,
        aggregatedSymbols: aggregatedSymbols,
        generatedBy: 'stewie_quant_dashboard',
        generatedAt: new Date().toISOString()
      },
      riskParameters: {
        maxPositionSizePct: currentConfig.maxPositionSizePct,
        maxStrategyExposurePct: 100,
        isVolWeightedRisk: true,
        allowDynamicExits: true,
        stopLossType: 'percentage',
        stopLossValue: '2',
        profitTargetType: 'percentage', 
        profitTargetValue: '5',
        atrPeriod: 14,
        volatilityPeriod: 20,
        correlationThreshold: currentConfig.correlationThreshold,
        zscoreThreshold: currentConfig.zscoreThreshold
      },
      backtestSettings: {
        mode: 'backtest',
        startDate: currentConfig.backtest.startDate,
        endDate: currentConfig.backtest.endDate,
        initialCapital: currentConfig.backtest.initialCapital.toString(),
        provider: 'alpaca',
        timeframe: 'day',
        environment: 'all',
        marketEnvironment: 'all',
        useRealTimeData: false,
        dataUpdateInterval: '60',
        includeCommission: false,
        commissionRate: '0',
        includeSlippage: false,
        slippageModel: 'fixed',
        slippageValue: '0',
        executionDelay: '0'
      }
    };

    onCreateStrategy(strategyData);
    
    toast({
      title: "Strategy Created",
      description: `StewieQuant strategy created with ${aggregatedSymbols.length} symbols and custom parameters.`,
    });
  };

  return (
    <div className="bg-white rounded-lg border shadow-sm flex flex-col h-full">
      <div className="p-4 border-b flex justify-between items-center">
          <div className="flex items-center gap-2">
            <LineChart className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-medium">StewieQuant™ AI</h2>
          </div>
          <div className="flex items-center gap-2">
            {modelStatus === 'completed' && (
              <div className="relative">
                <div className="absolute inset-0 bg-black bg-opacity-50 rounded flex items-center justify-center z-10">
                  <span className="text-white text-xs font-medium px-2 py-1">Coming Soon</span>
                </div>
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleCreateStrategy}
                  className="text-xs sm:text-sm bg-blue-600 hover:bg-blue-700 text-white relative"
                >
                  <Plus className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                  Create Strategy
                </Button>
              </div>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded?.(!isExpanded)}
              className="text-blue-600 hover:text-blue-700 text-xs sm:text-sm"
            >
              {isExpanded ? (
                <>
                  <span className="hidden sm:inline">Collapse</span>
                  <ChevronLeft className="w-4 h-4" />
                </>
              ) : (
                <>
                  <span className="hidden sm:inline">Expand</span>
                  <ChevronRight className="w-4 h-4" />
                </>
              )}
            </Button>
          </div>
        </div>

        <div className="p-4 flex-1 overflow-auto">
          {isExpanded ? (
            <div className="space-y-6">
              {/* Symbols Configuration - Always Visible */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">Symbol Configuration</h3>
                  <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded">
                    {aggregatedSymbols.length} symbols
                  </span>
                </div>
                <StewieQuantSymbolsAggregation 
                  onSave={handleSaveSymbols}
                  initialSymbols={aggregatedSymbols}
                  onFetchMarketData={onFetchMarketData || fetchMarketData}
                />
              </div>

              {/* Advanced Configuration - Collapsible */}
              <Collapsible open={showAdvancedConfig} onOpenChange={setShowAdvancedConfig}>
                <CollapsibleTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="w-full flex items-center justify-between"
                  >
                    <div className="flex items-center gap-2">
                      <Settings className="w-4 h-4" />
                      Advanced Configuration
                    </div>
                    {showAdvancedConfig ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="space-y-4">
                  <div className="pt-4">
                    <StewieQuantStrategyConfig 
                      onSave={handleSaveConfig}
                      initialConfig={currentConfig}
                    />
                  </div>
                </CollapsibleContent>
              </Collapsible>

              {/* Run Analysis Button - Always Visible and Prominent */}
              <div className="flex flex-col gap-4">
                <Button
                  onClick={handleRunOptimization}
                  disabled={aggregatedSymbols.length === 0 || modelStatus === 'running'}
                  className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white text-lg font-medium"
                  size="lg"
                >
                  {modelStatus === 'running' ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Running Analysis...
                    </>
                  ) : (
                    <>
                      <Play className="w-5 h-5 mr-2" />
                      Run StewieQuant Analysis
                    </>
                  )}
                </Button>
                
                {aggregatedSymbols.length === 0 && (
                  <p className="text-sm text-gray-500 text-center">
                    Add at least one symbol above to run analysis
                  </p>
                )}
              </div>

              {/* Results Section - Shows when analysis is run */}
              {showResults && (
                <div className="space-y-4">
                  <div className="border-t pt-6">
                    <h3 className="text-lg font-semibold mb-4">Analysis Results</h3>
                    <StewieQuantModelComponent 
                      strategy={{ 
                        ...activeStrategy, 
                        params: currentConfig,
                        symbols: aggregatedSymbols.map(s => s.symbol).join(',')
                      }}
                      onResults={handleModelResults}
                      onStatusChange={handleModelStatusChange}
                    />
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center space-y-4 py-8">
              <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center">
                <Settings className="w-8 h-8 text-blue-600" />
              </div>
              
              <h3 className="text-lg font-semibold text-center">StewieQuant™ AI</h3>
              
              <p className="text-sm text-gray-500 text-center max-w-[220px]">
                Configure symbols and run AI-powered trading analysis
              </p>
              
              <Button
                onClick={() => setIsExpanded?.(true)}
                className="mt-4 bg-blue-600 hover:bg-blue-700 text-white transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 rounded-[4px]"
              >
                Configure StewieQuant
              </Button>
            </div>
          )}
        </div>
    </div>
  );
};

export default StewieQuantDashboardIntegration;
